import { NOTE_PARENT_SUBJECTS } from "@msuite/citadel";
import type { NotesTable, Tables } from "@msuite/utils/types";
import { useCitadel } from "./use-citadel";

/**
 * What the caller may do with notes, in the shape the note surfaces ask.
 *
 * Three different questions, because the server asks three different ones. Reading a parent's
 * notes — and writing one, which asks the same right — follows the **parent's** subject, since a
 * note carries no reference to what it is about and no rule about notes could express it.
 * Correcting and withdrawing are asked per note, because both reach only what the caller wrote;
 * they deliberately do not ask the parent a second time, so a note stays its author's even where
 * its surroundings have gone out of reach.
 */
export function useNoteCapabilities() {
	/** Hooks */
	const { citadel, isError } = useCitadel();

	/** Functions */
	/**
	 * Whether the caller may read the thing a note surface hangs on, which is what decides both the
	 * list and the form. `parent` is the parent row: pass it wherever the page has it, because a
	 * rule narrower than the organisation — a calculation of one's own, a project one is on — can
	 * only be answered against the row. Without it the answer is the category one, the same the
	 * route's own gate would give, and the server still decides the row.
	 */
	function canReadParent(notesTable: NotesTable, parent?: Record<string, unknown> | null) {
		const subject = NOTE_PARENT_SUBJECTS[notesTable];
		// A parent with no subject is nobody's to withhold: the server proves it belongs to the
		// caller's organisation and asks nothing further.
		if (!subject) return true;
		return citadel?.can("read", subject, parent ?? undefined) === true;
	}

	/** Return */
	return {
		// The third of the three states `Can` documents. Every answer above reads false while the
		// rules are in flight, and a surface that draws a refusal from that tells the caller they
		// are locked out of something they may well hold. It is false once the rules have failed
		// to load too, because there is nothing left to wait for.
		rulesAreLoading: !citadel && !isError,
		canReadParent,
		canCreate: citadel?.can("create", "Note") === true,
		canUpdate: (note: Tables<"notes">) => citadel?.can("update", "Note", note) === true,
		canDelete: (note: Tables<"notes">) => citadel?.can("delete", "Note", note) === true,
	};
}
